/**
 * nudgeDeepLink — maps an in-app notification (nudge) to the in-app surface it
 * should open, derived from its ``nudge_type`` + ``payload``.
 *
 * Authored once here so EVERY nudge surface (the header bell popover and the
 * ``/workspace/work`` "Notifiche di Ando" clusters) deep-links identically —
 * no per-surface link logic, no duplicated route strings.
 *
 * Today the only mapped type is ``connect_space_shared`` (Ando Connect
 * cross-org phase): another org shared a Space with the current user, who is
 * now a cross-org MEMBER — the link opens that Space's member view. Unknown
 * types return ``null`` so the row renders as a plain, non-navigable
 * notification (forward-safe for BE types the FE doesn't model yet).
 *
 * The result is a TanStack-router-typed ``{ to, params }`` pair (NOT a flat
 * URL string) so callers spread it straight onto a typed ``<Link>`` and keep
 * full route type-safety.
 */

import type { NudgeInboxItem } from "@ando/core";

/** Typed router target for a navigable nudge — spread onto a ``<Link>``. */
export interface NudgeLinkTarget {
  readonly to: "/workspace/connect/$spaceId";
  readonly params: { readonly spaceId: string };
}

/** Read a string field off the free-form nudge payload defensively. */
function payloadString(payload: NudgeInboxItem["payload"], key: string): string | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = (payload as Record<string, unknown>)[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

/**
 * Resolve the in-app target a nudge should deep-link to, or ``null`` when the
 * type isn't navigable.
 */
export function nudgeDeepLink(nudge: NudgeInboxItem): NudgeLinkTarget | null {
  if (nudge.nudge_type === "connect_space_shared") {
    const spaceId = payloadString(nudge.payload, "space_id");
    if (spaceId) {
      return { to: "/workspace/connect/$spaceId", params: { spaceId } };
    }
  }
  return null;
}
